import type { QueryClient } from "@tanstack/react-query";

import {
  LICENSE_DETAIL_QUERY_IDS,
  LICENSE_LIST_QUERY_IDS,
  LICENSE_MAP_PINS_QUERY_IDS,
  LICENSE_STATS_QUERY_IDS,
  queryKeyOperationId,
} from "./license-list-query-keys";

const SELF_MUTATED_TTL_MS = 5000;
const selfMutated = new Map<string, number>();

export function recordSelfMutatedLicenseIds(licenseIds: string[]): void {
  const now = Date.now();
  for (const id of licenseIds) {
    if (id) selfMutated.set(id, now);
  }
}

export function consumeSelfMutatedLicenseIds(licenseIds: string[]): string[] {
  const now = Date.now();
  const remaining: string[] = [];
  for (const id of licenseIds) {
    const at = selfMutated.get(id);
    if (at != null && now - at < SELF_MUTATED_TTL_MS) {
      selfMutated.delete(id);
      continue;
    }
    remaining.push(id);
  }
  return remaining;
}

function matchesAnyId(
  queryKey: readonly unknown[],
  ids: readonly string[],
): boolean {
  if (ids.length === 0) return false;
  return ids.some((id) =>
    queryKey.some((part) => {
      if (typeof part === "string") return part.includes(id);
      if (part && typeof part === "object") {
        return JSON.stringify(part).includes(id);
      }
      return false;
    }),
  );
}

/**
 * Refresh list page, stats, map pins, and details after a local mutation.
 *
 * Marks every cached list/stats/pin query stale, but refetches only active ones.
 * This matters for mutations on separate routes: the dashboard query is
 * unmounted during the mutation and must refetch when the user navigates back.
 */
export async function refreshLicensesAfterLocalChange(
  queryClient: QueryClient,
  licenseIds: string | string[] | null | undefined,
): Promise<void> {
  const ids = (
    Array.isArray(licenseIds) ? licenseIds : licenseIds ? [licenseIds] : []
  ).filter(Boolean);
  recordSelfMutatedLicenseIds(ids);

  const invalidateByIds = (operationIds: readonly string[]) =>
    queryClient.invalidateQueries({
      refetchType: "active",
      predicate: (query) => {
        const id = queryKeyOperationId(query.queryKey);
        return id != null && operationIds.includes(id);
      },
    });

  await Promise.all([
    invalidateByIds(LICENSE_LIST_QUERY_IDS),
    invalidateByIds(LICENSE_STATS_QUERY_IDS),
    invalidateByIds(LICENSE_MAP_PINS_QUERY_IDS),
    ids.length === 0
      ? Promise.resolve()
      : queryClient.invalidateQueries({
          predicate: (query) => {
            const id = queryKeyOperationId(query.queryKey);
            return (
              id != null &&
              LICENSE_DETAIL_QUERY_IDS.includes(
                id as (typeof LICENSE_DETAIL_QUERY_IDS)[number],
              ) &&
              matchesAnyId(query.queryKey, ids)
            );
          },
        }),
  ]);
}

/** @deprecated Use {@link refreshLicensesAfterLocalChange}. */
export async function notifyLicenseDetailsChanged(
  queryClient: QueryClient,
  licenseId: string | string[] | null | undefined,
): Promise<void> {
  await refreshLicensesAfterLocalChange(queryClient, licenseId);
}
